<template>
    <section class="card meta-section">
        <div class="section-title-row">
            <h1 class="section-title">
                {{ $trans("web_skill_ranking.meta_title") || "Meta Title" }}
            </h1>

            <div class="button-group">
                <el-button
                    type="primary"
                    :loading="WebSkillRankingStore.isGeneratingMetaTitle"
                    :disabled="WebSkillRankingStore.isGeneratingMetaTitle || WebSkillRankingStore.isGeneratingMetaDescription || isAnyGenerating"
                    @click="generateAndSaveMetaTitle"
                    class="generate-btn"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="mr-2"
                    >
                        <path
                            d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"
                        />
                    </svg>
                    {{
                        $trans("web_skill_ranking.generate_meta_title") ||
                        "AI Generate"
                    }}
                </el-button>

                <EditButton
                    :onClick="WebSkillRankingStore.openEditMetaTitleModal"
                />
            </div>
        </div>

        <div class="details-header">
            <div class="meta-content">
                <div
                    v-if="
                        WebSkillRankingStore.ranking.meta_title || !WebSkillRankingStore.isGeneratingMetaTitle
                    "
                    class="meta-text"
                    style="white-space: pre-wrap"
                >
                    <span v-if="WebSkillRankingStore.ranking.meta_title">
                        {{ WebSkillRankingStore.ranking.meta_title }}
                    </span>
                    <span v-else class="empty-text">
                        {{
                            $trans("web_skill_ranking.add_meta_title") ||
                            "Add Meta Title"
                        }}
                    </span>
                </div>

                <el-skeleton
                    v-if="
                        WebSkillRankingStore.isGeneratingMetaTitle && !WebSkillRankingStore.ranking.meta_title
                    "
                    animated
                    :rows="1"
                    style="width: 100%"
                />
            </div>
        </div>

        <EditMetaTitleModal />
    </section>
</template>

<script>
import { mapStores } from "pinia";
import { useWebSkillRankingStore } from "@/stores/WebSkillRankingStore";
import EditButton from "./../../WebProfile/Components/EditButton.vue";
import EditMetaTitleModal from "./../Modals/EditMetaTitleModal.vue";

export default {
    name: "MetaTitleSection",
    components: { EditButton, EditMetaTitleModal },
    computed: {
        ...mapStores(useWebSkillRankingStore),
        isAnyGenerating() {
            return this.WebSkillRankingStore.profiles.some(
                (p) => p.ai_content_status === "in_progress",
            );
        },
    },
    methods: {
        async generateAndSaveMetaTitle() {
            if (this.WebSkillRankingStore.isGeneratingMetaTitle || this.WebSkillRankingStore.isGeneratingMetaDescription || this.isAnyGenerating) {
                return;
            }

            this.WebSkillRankingStore.isGeneratingMetaTitle = true;

            const oldMetaTitle = this.WebSkillRankingStore.ranking.meta_title;

            this.WebSkillRankingStore.ranking.meta_title = "";

            try {
                const rankingId = this.WebSkillRankingStore.ranking.id;

                if (!rankingId) {
                    return;
                }

                const csrfToken = document
                    .querySelector('meta[name="csrf-token"]')
                    ?.getAttribute("content");

                const response = await fetch(
                    `/admin/web-skill-rankings/${rankingId}/generate/meta-title`,
                    {
                        method: "GET",
                        credentials: "same-origin",
                        headers: {
                            "X-Requested-With": "XMLHttpRequest",
                            "X-CSRF-TOKEN": csrfToken,
                            Accept: "text/plain",
                        },
                    },
                );

                if (!response.ok) {
                    this.WebSkillRankingStore.ranking.meta_title = oldMetaTitle;

                    if (response.status === 429) {
                        this.$message.warning(
                            this.$trans("web_skill_ranking.ai_busy") ||
                                "AI is busy, please try again later.",
                        );
                    } else {
                        this.$message.error(
                            `AI server error (${response.status})`,
                        );
                    }

                    return;
                }

                if (!response.body) {
                    throw new Error("No response body");
                }

                const reader = response.body.getReader();

                const decoder = new TextDecoder("utf-8");

                let fullContent = "";

                while (true) {
                    const { done, value } = await reader.read();

                    if (done) {
                        break;
                    }

                    const chunk = decoder.decode(value, {
                        stream: true,
                    });

                    console.log("STREAM CHUNK:", chunk);

                    fullContent += chunk;

                    this.WebSkillRankingStore.ranking.meta_title =
                        fullContent.trim();
                }

                fullContent = fullContent.trim();

                if (fullContent) {
                    await this.WebSkillRankingStore.updateRankingMetaTitle(
                        fullContent,
                    );

                    this.$message.success("Meta title generated");
                }
            } catch (error) {
                console.error("Error generating meta title:", error);

                this.WebSkillRankingStore.ranking.meta_title = oldMetaTitle;

                this.$message.error("AI generation failed");
            } finally {
                this.WebSkillRankingStore.isGeneratingMetaTitle = false;
            }
        },
    },
};
</script>

<style scoped>
.meta-section {
    padding: 1.5rem;
    border: 1px solid #ddd;
    border-radius: 8px;
    background-color: #fff;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 20px;
}
.section-title-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
}
.section-title {
    margin: 0;
    font-size: 1.875rem;
    font-weight: 600;
}
.details-header {
    display: flex;
    width: 100%;
}
.meta-content {
    flex: 1;
}
.meta-text {
    margin: 0;
    font-size: 16px;
    word-break: break-all;
}
.empty-text {
    color: #999;
    font-style: italic;
    font-size: 16px;
}
.button-group {
    display: flex;
    gap: 10px;
    flex-shrink: 0;
    align-items: center;
}
.generate-btn {
    background-color: #41abc4;
    border-color: #41abc4;
    color: white;
    cursor: pointer;
    font-size: 16px !important;
    font-weight: 700 !important;
}
.generate-btn:hover {
    background-color: #3693a9;
    border-color: #3693a9;
}
.generate-btn.is-disabled {
    background-color: #999 !important;
    border-color: #999 !important;
    cursor: not-allowed !important;
}
.mr-2 {
    margin-right: 0.5rem;
}
</style>